import React, { useEffect } from "react";
import { openFeedsFiltersOverlay } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import { ReduxStoreState } from "web/redux/store";
import { closeGenericModal, GenericModal } from "web/redux/ducks/send-to-app-modal";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { RawBranchLink } from "web/script/utils/branch-io";
import globals from "web/script/modules/globals";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import history from "web/script/utils/history";

function extractState(state: ReduxStoreState): GenericModal {
    return state.sendToAppModalReducer.genericModal;
}

const APP_MODAL_NAME = "event-counter-block";
const TITLE_KEYS = {
    // "Lyst is even better in the App"
    title_1: gettextNoop("feed.events.app_overlay_block.title_1"),
    // "Find what you're looking for faster with the Lyst App"
    title_2: gettextNoop("feed.events.app_overlay_block.title_2"),
    // "Shop the world’s biggest and best selection of fashion"
    title_3: gettextNoop("feed.events.app_overlay_block.title_3"),
    // "The hottest and newest products from over 12,000 brands"
    title_4: gettextNoop("feed.events.app_overlay_block.title_4"),
};

export default function SendToAppModalContainer(): JSX.Element | null {
    const dispatch = useDispatch();
    const { openModal, modalType, paywalledClassName, product, next } = useReduxSelector(
        extractState
    );
    const keepModalOpen = !!useOverlayWatcher(APP_MODAL_NAME);
    useEffect(() => {
        if (!keepModalOpen) {
            dispatch(closeGenericModal());
            history.popOverlay(APP_MODAL_NAME);
        }
    }, [keepModalOpen, dispatch]);

    useEffect(() => {
        if (openModal) {
            // I check the current overlay state instead of modalIsOpen
            // to get around the delay introduced by hooks
            if (history.getCurrentState().overlay != APP_MODAL_NAME) {
                history.pushOverlay(APP_MODAL_NAME);
            }
        }
    }, [openModal]);
    let fullProductUrl;
    if (product?.productUrl) {
        try {
            fullProductUrl = new URL(product.productUrl, globals.window.location.href);
        } catch (err) {
            console.error(`${product.productUrl} is not a valid URL`);
        }
    }

    let branchLink = "";
    let blockVariant;
    let title = "";
    let analyticsEventLabel;
    let continueOption;

    function onClose(): void {
        history.popOverlay(APP_MODAL_NAME);
    }

    function continueClick(): void {
        if (next) {
            if (next.type === "openFilter") {
                openFeedsFiltersOverlay();
            } else if (next.type === "showMore" && next.href) {
                const pageViewId = history.getCurrentState().pageViewId;
                history.pushState(new URL(next.href, globals.window.location.href), {
                    saveScrollPosition: false,
                    data: { pageViewId },
                });
            }
        }
        onClose();
    }

    if (modalType === "event_counter") {
        const titleVariant = String(environment.getFeature("mg_block_on_event_counter_title"));
        blockVariant = environment.getFeature("mg_block_on_event_counter");
        title = gettext(TITLE_KEYS[titleVariant]);
        branchLink = RawBranchLink.AppSignupEventCounter;
        analyticsEventLabel = "event_counter_block";

        // testing a different soft block experience - 'continue shopping' will take the user to the product
        // or will continue with the original action (show more/filter open)
        if (blockVariant === "soft_block") {
            continueOption = (
                <ContinueToOption
                    href={fullProductUrl}
                    continueMessage={gettext("general.continue_shopping_btn_text")}
                    analyticsEventLabel={analyticsEventLabel}
                    continueClick={continueClick}
                />
            );
        }
    }

    if (!modalType) {
        return null;
    } else {
        return (
            <SendToAppModal
                isOpen={openModal}
                onClose={onClose}
                title={title}
                url={branchLink}
                analyticsEventLabel={analyticsEventLabel}
                paywalledClassName={paywalledClassName}
                productImageURL={product?.productImageUrl}
                extraContent={(): JSX.Element => {
                    return continueOption;
                }}
            />
        );
    }
}
